import TravelHiringAnswers from '@/modules/travel/hiring/models/APP/TravelHiringAnswers'
import Summary from '@/modules/common/hiring/models/APP/Summary'
import { format } from '@/utils/date.utils'
import CommonMapper from '@/modules/core/mappers/CommonMapper'
import type { SummaryType3Interface, SummaryType1Interface } from '@/modules/core/models/types'
import { priceFormat } from '@/utils/textUtils'
import { useTravelStore } from '@/modules/travel/hiring/stores'
import { UserRoleEnum } from '@/modules/core/models/enums'
import { useSummaryComposable } from '@/modules/core/composables/useSummaryComposable'
import type { PricePeriodType } from '@/modules/common/common/models/APP/types'

export default class TravelHiringSummary implements Summary {
  private answers: TravelHiringAnswers
  private t
  private travelStore
  private summaryComposable

  constructor(data: TravelHiringAnswers, t: (key: string, values?: any) => string) {
    this.answers = data

    this.t = t
    this.travelStore = useTravelStore()
    this.summaryComposable = useSummaryComposable(t)
  }

  #getStep0Summary(): SummaryType1Interface[] {
    const config = this.travelStore.getBranchConfiguration()
    let billingCicle = this.answers.billingCycle
      ? CommonMapper.toDXPProductPrice(this.answers.billingCycle)
      : ''
    if (billingCicle === 'Unica') {
      billingCicle = this.t('common.periods.unica')
    }
    return [
      {
        label: this.t('copy_summary_step0_date_label'),
        value: format(this.answers.startDate),
        visible: !!this.answers.startDate
      },
      {
        label: this.t('copy_summary_step0_billing_label'),
        value: billingCicle,
        visible: !!this.answers.billingCycle
      },
      {
        label: this.t('copy_summary_step0_num-insureds_label'),
        value: this.t('components.InsuredSubheaderComponent.label', this.answers.insureds?.length),
        visible: !!this.answers.insureds?.length
      },
      {
        label: this.t('copy_summary_step0_destination_label'),
        value:
          config.destinationTypesOptions.find(
            (d) => d.value === String(this.answers.destinationType)
          )?.label ?? '',
        visible: !!this.answers.destinationType
      },
      {
        label: this.t(
          `copy_summary_step0_departure_${
            String(this.answers.insuranceType) === config.insuranceTypes.several.id
              ? 'allyear_'
              : ''
          }date_label`
        ),
        value: this.answers.departureDate ? format(this.answers.departureDate) : '',
        visible: !!this.answers.departureDate
      },
      {
        label: this.t(
          `copy_summary_step0_return_${
            String(this.answers.insuranceType) === config.insuranceTypes.several.id
              ? 'allyear_'
              : ''
          }date_label`
        ),
        value: this.answers.returnDate ? format(this.answers.returnDate) : '',
        visible: !!this.answers.returnDate
      }
    ]
  }

  private getInsuredLabel(role: string): string {
    if (role === UserRoleEnum.HOLDER_INSURED) {
      return 'holder-insured'
    }
    if (role === UserRoleEnum.HOLDER_NOT_INSURED) {
      return 'holder'
    }
    return 'insured'
  }

  private getIndex(role: string, index: number, holderIsNotInsured: boolean): number {
    if (holderIsNotInsured && role === UserRoleEnum.INSURED) {
      return index
    }
    return index + 1
  }

  #getInsuredsData(subStepIndex?: number): SummaryType3Interface[] {
    const insureds = [...this.answers.insureds]
    let holderIsNotInsured = false
    if (this.answers.holder.role === UserRoleEnum.HOLDER_NOT_INSURED) {
      insureds.unshift(this.answers.holder)
      holderIsNotInsured = true
    }

    return insureds.map((insured, i) => {
      const { userDetails, role } = insured

      const i18nLabel = this.getInsuredLabel(role)
      const labelInsured = this.t(
        `hiring.common.${i18nLabel}`,
        this.getIndex(role, i, holderIsNotInsured)
      )
      const label = userDetails.name ? userDetails.getFullName() : ''
      const page = `Step1_${i}_1_InsuranceAnotherCompanyDataForm`
      const subStep1 = this.summaryComposable.getDocumentInsuredStep(insured)
      const subStep2 = [
        this.summaryComposable.getBirthDateStep(insured),
        this.summaryComposable.getGenderStep(
          insured,
          this.travelStore.getBranchConfiguration().genderOptions
        ),
        this.summaryComposable.getAddressStep(insured),
        this.summaryComposable.getEmailStep(insured),
        this.summaryComposable.getPhoneStep(insured)
      ]
      const stepsToFilter = [subStep1, subStep2]
      const currentRiskNumber = this.answers.currentInsured?.riskNumber
      const subSteps = this.summaryComposable.filterInsuredsRenderableSteps(
        insured,
        currentRiskNumber!,
        stepsToFilter,
        subStepIndex
      )

      return {
        label: label,
        visible: subSteps.length > 0 && !!label,
        page,
        subLabel: labelInsured,
        values: subSteps
      }
    })
  }

  getProductName() {
    return this.answers.product?.title ?? ''
  }

  getPrice() {
    const product = this.answers.product
    const price = priceFormat(this.answers.price)

    const config = this.travelStore.getBranchConfiguration()
    const billingCycle =
      config.billingCycles?.[(this.answers.billingCycle as PricePeriodType) ?? 'unique']
        ?.textInPrice ?? ''

    return `${price}${product?.units}${billingCycle ? '/' + billingCycle : ''}`
  }

  getRequestNumber() {
    return String(this.answers.product?.quotationNumber)
  }

  getStepsData(subStepIndex?: number) {
    return [
      {
        label: this.t('hiring.summary.health.step0.label'),
        type: 'TYPE1',
        items: this.#getStep0Summary(),
        page: 'Step0_1_StartDateForm'
      },
      {
        label: this.t('hiring.summary.health.step2.label'),
        type: 'TYPE3',
        items: this.#getInsuredsData(subStepIndex),
        page: 'Step1_0_1_InsuranceAnotherCompanyDataForm'
      },
      {
        label: this.t('hiring.summary.health.step4.label'),
        type: 'TYPE1',
        items: [],
        page: 'Step2_1_PaymentType'
      }
    ]
  }
}
